/**
 * createLead — web SDK equivalent of the mobile
 * `src/features/crm/leads.ts::createLead`.
 *
 * Writes a new document to the top-level `leads` Firestore
 * collection. Server-side rules
 * (`firestore.rules → match /leads/{leadId}`) enforce that the
 * caller has the CRM-write role for the supplied `orgId`; the client
 * just has to send the right shape.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';

import { db } from '@/lib/firebase';
import type {
  BudgetRange,
  LeadPriority,
  LeadSource,
  LeadStatus,
  PossessionStatus,
  ProjectType,
} from '@/lib/useLeads';

export type CreateLeadInput = {
  orgId: string;
  name: string;
  phone: string;
  source: LeadSource;
  status: LeadStatus;
  priority: LeadPriority;
  createdBy: string;

  email?: string;
  budget?: number;            // legacy free-number; new writes prefer budgetRange
  budgetRange?: BudgetRange;  // NEW
  possessionStatus?: PossessionStatus; // NEW
  projectType?: ProjectType;  // NEW (already in mobile)
  location?: string;
  notes?: string;
  followUpAt?: Date | null;
  expectedStartDate?: Date | null;
  assignedTo?: string;
  // `tags` intentionally NOT in the create payload — removed per the
  // redesigned form. Legacy docs may still have tags, but new writes
  // don't set them.
};

export async function createLead(input: CreateLeadInput): Promise<string> {
  const doc: Record<string, unknown> = {
    orgId: input.orgId,
    name: input.name,
    phone: input.phone,
    source: input.source,
    status: input.status,
    priority: input.priority,
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (input.email) doc.email = input.email;
  if (input.budget !== undefined && input.budget !== null) doc.budget = input.budget;
  if (input.budgetRange) doc.budgetRange = input.budgetRange;
  if (input.possessionStatus) doc.possessionStatus = input.possessionStatus;
  if (input.projectType) doc.projectType = input.projectType;
  if (input.location) doc.location = input.location;
  if (input.notes) doc.notes = input.notes;
  if (input.followUpAt) doc.followUpAt = Timestamp.fromDate(input.followUpAt);
  if (input.expectedStartDate) {
    doc.expectedStartDate = Timestamp.fromDate(input.expectedStartDate);
  }
  if (input.assignedTo) doc.assignedTo = input.assignedTo;

  const ref = await addDoc(collection(db, 'leads'), doc);
  return ref.id;
}

// ───── Update + Delete (web SDK port of the mobile helpers) ─────

/** Partial update — pass only the fields you want to change. Set a
 *  field to `null` (where the type allows) to CLEAR it via
 *  `deleteField()`. Mirrors mobile `updateLead` semantics 1:1. */
export type UpdateLeadInput = Partial<{
  name: string;
  phone: string;
  email: string | null;
  source: LeadSource;
  status: LeadStatus;
  priority: LeadPriority;
  location: string | null;
  budget: number | null;
  budgetRange: BudgetRange | null;
  possessionStatus: PossessionStatus | null;
  projectType: ProjectType | null;
  notes: string | null;
  followUpAt: Date | null;
  expectedStartDate: Date | null;
  assignedTo: string | null;
  /** Pass `null` to migrate-clear the legacy field. */
  tags: string[] | null;
}>;

export async function updateLead(id: string, data: UpdateLeadInput): Promise<void> {
  const updates: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (data.name !== undefined) updates.name = data.name;
  if (data.phone !== undefined) updates.phone = data.phone;
  if (data.email !== undefined) {
    updates.email = data.email === null ? deleteField() : data.email;
  }
  if (data.source !== undefined) updates.source = data.source;
  if (data.status !== undefined) updates.status = data.status;
  if (data.priority !== undefined) updates.priority = data.priority;
  if (data.location !== undefined) {
    updates.location = data.location === null ? deleteField() : data.location;
  }
  if (data.budget !== undefined) {
    updates.budget = data.budget === null ? deleteField() : data.budget;
  }
  if (data.budgetRange !== undefined) {
    updates.budgetRange = data.budgetRange === null ? deleteField() : data.budgetRange;
  }
  if (data.possessionStatus !== undefined) {
    updates.possessionStatus =
      data.possessionStatus === null ? deleteField() : data.possessionStatus;
  }
  if (data.projectType !== undefined) {
    updates.projectType = data.projectType === null ? deleteField() : data.projectType;
  }
  if (data.notes !== undefined) {
    updates.notes = data.notes === null ? deleteField() : data.notes;
  }
  if (data.followUpAt !== undefined) {
    updates.followUpAt =
      data.followUpAt === null ? deleteField() : Timestamp.fromDate(data.followUpAt);
  }
  if (data.expectedStartDate !== undefined) {
    updates.expectedStartDate =
      data.expectedStartDate === null
        ? deleteField()
        : Timestamp.fromDate(data.expectedStartDate);
  }
  if (data.assignedTo !== undefined) {
    updates.assignedTo = data.assignedTo === null ? deleteField() : data.assignedTo;
  }
  if (data.tags !== undefined) {
    updates.tags =
      data.tags === null || data.tags.length === 0 ? deleteField() : data.tags;
  }

  await updateDoc(doc(db, 'leads', id), updates);
}

export async function deleteLead(id: string): Promise<void> {
  await deleteDoc(doc(db, 'leads', id));
}
